// On-device receipt reading: OCR text -> { amount, date }, then rank matching transactions
import { addDays, today, round2 } from "./format";
import { merchantKey } from "./categorize";
import { createOcrWorker } from "./ocrWorker";

const MONTHS = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
const MONEY = /\$?\s?(\d{1,3}(?:,\d{3})+|\d+)\s?[.,]\s?(\d{2})(?!\d)/g;
const pad = (n) => String(n).padStart(2, "0");

const valid = (y, m, d) => {
  if (y < 100) y += 2000;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null;
  const iso = `${y}-${pad(m)}-${pad(d)}`;
  return iso <= addDays(today(), 1) ? iso : null;
};

export function findDate(text) {
  const t = text.replace(/[|]/g, "/");
  const tries = [
    [/\b(20\d{2})[-/.](\d{1,2})[-/.](\d{1,2})\b/, (m) => valid(+m[1], +m[2], +m[3])],
    [/\b(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/, (m) => valid(+m[3], +m[1], +m[2]) || valid(+m[3], +m[2], +m[1])],
    [/\b([a-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+(\d{2,4})\b/i, (m) => MONTHS[m[1].toLowerCase()] && valid(+m[3], MONTHS[m[1].toLowerCase()], +m[2])],
    [/\b(\d{1,2})\s+([a-z]{3})[a-z]*\.?,?\s+(\d{2,4})\b/i, (m) => MONTHS[m[2].toLowerCase()] && valid(+m[3], MONTHS[m[2].toLowerCase()], +m[1])],
  ];
  for (const [re, fn] of tries) {
    const m = t.match(re);
    const v = m && fn(m);
    if (v) return v;
  }
  return null;
}

const moneyIn = (line) => [...line.matchAll(MONEY)].map((m) => Number(`${m[1].replace(/,/g, "")}.${m[2]}`)).filter((v) => v > 0 && v < 100000);

export function findAmount(text) {
  const lines = text.split(/\n/);
  const ranks = [/grand\s*total|amount\s*due|balance\s*due|total\s*due|amount\s*paid/i, /^(?!.*sub).*\btotal\b/i, /\b(visa|mastercard|amex|debit|credit|card|charged)\b/i];
  for (const re of ranks) {
    const ranked = lines.filter((l) => re.test(l));
    const hits = ranked.flatMap(moneyIn);
    if (hits.length) return Math.max(...hits);
    // OCR often drops the decimal point: "TOTAL 2326" -> 23.26
    const bare = ranked.flatMap((l) => [...l.matchAll(/(?:^|\s)\$?(\d{3,6})(?=\s*$)/g)].map((m) => Number(m[1]) / 100));
    if (bare.length) return Math.max(...bare);
  }
  const all = lines.flatMap(moneyIn);
  return all.length ? Math.max(...all) : null;
}

// Store name = first header line that looks like words (not an address, phone, date or total)
export function findStore(text) {
  const lines = String(text || "").split(/\n/).map((l) => l.trim()).filter(Boolean).slice(0, 6);
  for (const l of lines) {
    const letters = (l.match(/[a-z]/gi) || []).length;
    if (letters < 3 || letters / l.replace(/\s/g, "").length < 0.6) continue;
    if (/^\d+\s|\b(st|street|ave|avenue|rd|road|blvd|suite|tel|phone|www|http|receipt|welcome)\b/i.test(l) || moneyIn(l).length) continue;
    return normStore(l);
  }
  return "";
}

export const normStore = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9&' ]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 40);

export const parseReceiptText = (text) => ({ amount: findAmount(text || ""), date: findDate(text || ""), store: findStore(text || "") });

const dayGap = (a, b) => Math.abs((new Date(a) - new Date(b)) / 86400000);

// Expenses near the amount/date; business expenses missing a receipt float to the top
export function matchReceipt(state, { amount, date, store }) {
  const amt = Math.abs(Number(amount) || 0);
  if (!amt && !date) return [];
  const link = store ? (state.vendorLinks || {})[normStore(store)] : null;
  return state.transactions
    .filter((t) => t.amount < 0 && !t.excluded)
    .map((t) => {
      const a = Math.abs(t.amount);
      const diff = amt ? Math.abs(a - amt) / amt : 0;
      const gap = date ? dayGap(t.date, date) : 0;
      if (amt && diff > 0.25) return null;
      if (date && gap > 10) return null;
      const needs = t.business && !(t.receipts || []).length;
      const learned = !!link && merchantKey(t.description) === link.merchant;
      return { tx: t, exact: amt ? round2(a) === round2(amt) : false, needs, learned, score: diff * 100 + gap - (needs ? 3 : 0) - (amt && round2(a) === round2(amt) ? 5 : 0) - (learned ? 8 : 0) };
    })
    .filter(Boolean)
    .sort((x, y) => x.score - y.score)
    .slice(0, 8);
}

// Inbox receipts with exactly one expense of the same amount on the same date (each expense used once)
export function exactPairs(state) {
  const taken = new Set();
  const pairs = [];
  let ambiguous = 0;
  [...(state.receiptInbox || [])].sort((a, b) => String(a.addedAt).localeCompare(String(b.addedAt))).forEach((it) => {
    const amt = round2(Math.abs(Number(it.amount) || 0));
    if (!amt || !it.date) return;
    const hits = state.transactions.filter((t) => t.amount < 0 && !t.excluded && t.date === it.date && round2(Math.abs(t.amount)) === amt && !taken.has(t.id));
    if (hits.length === 1) {
      taken.add(hits[0].id);
      pairs.push({ itemId: it.id, txId: hits[0].id });
    } else if (hits.length > 1) ambiguous++;
  });
  return { pairs, ambiguous };
}

// Upscale small photos and convert to grayscale — tesseract reads small receipt text far better
async function prepare(file) {
  try {
    const bmp = await createImageBitmap(file);
    const long = Math.max(bmp.width, bmp.height);
    const scale = long < 1200 ? Math.min(3, 1600 / long) : 1;
    const c = document.createElement("canvas");
    c.width = Math.round(bmp.width * scale);
    c.height = Math.round(bmp.height * scale);
    const g = c.getContext("2d");
    g.filter = "grayscale(1) contrast(1.2)";
    g.imageSmoothingQuality = "high";
    g.drawImage(bmp, 0, 0, c.width, c.height);
    return c;
  } catch {
    return file;
  }
}

export async function ocrImage(file, onProgress) {
  const worker = await createOcrWorker((m) => m.status === "recognizing text" && onProgress?.(Math.round(m.progress * 100)));
  try {
    const { data } = await worker.recognize(await prepare(file));
    return data.text;
  } finally {
    await worker.terminate();
  }
}
